import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import {CButton, CInput, CSelect, CTextarea} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {getApi} from "../../apis/backendApis";
import useAlert from "../../services/useAlert";
import useLoading from "../../services/useLoading";
import {Processo, erroDaApi} from "./modelo";
import VersaoFluxoModal from "./versaoFluxoModal";
import '../../scss/styles-processo.scss';

/**
 * Alterar o processo (#484909), pela acao "Alterar" da grade de processos: os
 * dados do primeiro passo da abertura. O cliente nao muda (o processo foi
 * gerado para ele); o fluxo so pode ir para a versao atual (#485540), pela
 * previa da atualizacao; a data de inicio so antes de qualquer execucao, e o
 * cronograma inteiro anda junto. So o gestor (ou master) salva.
 */
const ProcessoAlterar = () => {
    const {id} = useParams<{id: string}>();
    const history = useHistory();
    const [showAlert] = useAlert();
    const [setLoading] = useLoading();
    const [processo, setProcesso] = useState<Processo | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [usuarios, setUsuarios] = useState<Array<{id: number, nome: string}>>([]);
    const [webhooks, setWebhooks] = useState<Array<{id: number, nome: string}>>([]);
    const [titulo, setTitulo] = useState('');
    const [complexidade, setComplexidade] = useState<number | ''>('');
    const [gestorId, setGestorId] = useState<number>(0);
    const [webhookId, setWebhookId] = useState<number | ''>('');
    const [inicio, setInicio] = useState('');
    const [motivo, setMotivo] = useState('');
    const [versaoAberta, setVersaoAberta] = useState(false);

    useEffect(() => {
        const api = getApi();
        setLoading(true);
        api.get(`processo/get/${id}`)
            .then(r => {
                const dados = r.data as Processo;
                setProcesso(dados);
                setTitulo(dados.titulo);
                setComplexidade(dados.complexidade ?? '');
                setGestorId(dados.gestor.id);
                setWebhookId(dados.webhook_mattermost?.id ?? '');
                setInicio(dados.data_inicio);
            })
            .catch(e => setErro(erroDaApi(e, 'Não foi possível carregar o processo.')))
            .finally(() => setLoading(false));
        api.get('/usuarios/suggest').then(resposta => {
            setUsuarios((resposta.data.usuarios || []).map((par: any) => {
                const [idUsuario, nome] = Object.entries(par)[0];
                return {id: Number(idUsuario), nome: String(nome)};
            }));
        }).catch(() => setUsuarios([]));
        api.get('processo/webhooks-mattermost').then(r => setWebhooks(r.data.webhooks || [])).catch(() => setWebhooks([]));
    }, [id]);

    const voltar = () => history.push('/processos');

    if (erro) {
        return <div className="pr-novo"><div className="pr-vazio">{erro}</div></div>;
    }
    if (!processo) {
        return null;
    }

    const ativo = processo.situacao === 1 || processo.situacao === 2;
    const editavel = ativo && processo.pode_gerir;
    const mudou = titulo.trim() !== processo.titulo || (complexidade || null) !== (processo.complexidade ?? null) || gestorId !== processo.gestor.id
        || (webhookId || null) !== (processo.webhook_mattermost?.id ?? null) || inicio !== processo.data_inicio;

    const salvar = () => {
        const dados: any = {titulo, complexidade: complexidade || null, gestor_id: gestorId, webhook_mattermost_id: webhookId || null, motivo};
        if (processo.inicio_alteravel && inicio !== processo.data_inicio) dados.data_inicio = inicio;
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(dados));
        setLoading(true);
        getApi().post(`processo/alterar/${processo.id}`, corpo)
            .then(resposta => {
                showAlert({msg: resposta.data.msg ?? 'Processo alterado.', type: 'success', toaster: true});
                voltar();
            })
            .catch(e => showAlert({title: 'Não foi possível', msg: erroDaApi(e, 'Erro ao alterar o processo.'), type: 'danger', toaster: true}))
            .finally(() => setLoading(false));
    };

    return (
        <div className="pr-novo">
            <header className="pr-novo-topo">
                <button type="button" className="pr-voltar" onClick={voltar} title="Voltar"><CIcon name="cil-arrow-left"/></button>
                <h4>Alterar processo #{processo.id}</h4>
            </header>
            <section className="pr-novo-corpo pr-alterar">
                {!ativo && <div className="pr-aviso">Processo {processo.situacao_nome.toLowerCase()}: não aceita alterações.</div>}
                {ativo && !processo.pode_gerir && <div className="pr-aviso">Só o gestor do processo ({processo.gestor.nome}) pode alterá-lo.</div>}
                <div className="pr-form">
                    <div className="pr-evento-grade">
                        <label className="largo">
                            <span>Cliente</span>
                            <CInput value={processo.cliente.nome} disabled/>
                        </label>
                        <label>
                            <span>Fluxo</span>
                            <CInput value={`${processo.fluxo.codigo} · v${processo.fluxo.versao}`} disabled/>
                        </label>
                        {processo.fluxo_desatualizado && (
                            <div className="pr-aviso pr-versao-aviso inteiro">
                                <span>
                                    O fluxo {processo.fluxo.codigo} mudou: está na versão {processo.fluxo.versao_atual}, e este processo segue a {processo.fluxo.versao}.
                                    {editavel ? ' Veja o que muda antes de atualizar.' : ''}
                                </span>
                                {editavel && (
                                    <CButton size="sm" color="warning" onClick={() => setVersaoAberta(true)}>
                                        <CIcon name="cil-reload"/> Atualizar para a v{processo.fluxo.versao_atual}
                                    </CButton>
                                )}
                            </div>
                        )}
                        <p className="pr-ajuda inteiro pr-alterar-nota">
                            O cliente não muda: o processo (itens, papéis e portal do cliente) foi gerado para ele. O fluxo só muda de versão:
                            o que já foi respondido fica como está, e o histórico guarda tudo.
                        </p>
                        <label>
                            <span>Início</span>
                            <CInput type="date" value={inicio} disabled={!editavel || !processo.inicio_alteravel} onChange={(e: any) => setInicio(e.target.value)}/>
                        </label>
                        <label>
                            <span>Complexidade</span>
                            <CSelect value={complexidade} disabled={!editavel} onChange={(e: any) => setComplexidade(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">—</option>
                                <option value={1}>Baixa</option>
                                <option value={2}>Média</option>
                                <option value={3}>Alta</option>
                            </CSelect>
                        </label>
                        <label>
                            <span>Gestor (Gerente de Projetos)</span>
                            <CSelect value={gestorId} disabled={!editavel} onChange={(e: any) => setGestorId(Number(e.target.value))}>
                                {!usuarios.some(u => u.id === processo.gestor.id) && <option value={processo.gestor.id}>{processo.gestor.nome}</option>}
                                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                            </CSelect>
                        </label>
                        <p className="pr-ajuda inteiro pr-alterar-nota">
                            {processo.inicio_alteravel
                                ? 'Mudar o início anda o cronograma inteiro (meta e previsão) os mesmos dias úteis, como se o processo tivesse sido aberto na data nova.'
                                : 'O início só muda antes de qualquer execução. Agora, para mudar datas, reprograme as etapas no Cronograma do processo.'}
                        </p>
                        <label className="largo">
                            <span>Título</span>
                            <CInput value={titulo} maxLength={150} disabled={!editavel} onChange={(e: any) => setTitulo(e.target.value)}
                                    placeholder={`${processo.fluxo.nome} — ${processo.cliente.nome}`}/>
                        </label>
                        <label>
                            <span>Avisar no Mattermost</span>
                            <CSelect value={webhookId} disabled={!editavel} onChange={(e: any) => setWebhookId(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Não avisar no Mattermost</option>
                                {processo.webhook_mattermost && !webhooks.some(w => w.id === processo.webhook_mattermost!.id) && (
                                    <option value={processo.webhook_mattermost.id}>{processo.webhook_mattermost.nome}</option>
                                )}
                                {webhooks.map(w => <option key={w.id} value={w.id}>{w.nome}</option>)}
                            </CSelect>
                        </label>
                        <label className="inteiro">
                            <span>Motivo (opcional, vai para o histórico)</span>
                            <CTextarea rows={2} value={motivo} disabled={!editavel} onChange={(e: any) => setMotivo(e.target.value)}/>
                        </label>
                    </div>
                    <div className="pr-alterar-acoes">
                        <CButton color="secondary" variant="outline" onClick={voltar}>Cancelar</CButton>
                        <CButton color="primary" disabled={!editavel || !mudou} onClick={salvar}>
                            <CIcon name="cil-save"/> Salvar
                        </CButton>
                    </div>
                </div>
            </section>
            {versaoAberta && (
                <VersaoFluxoModal processo={processo} onFechar={() => setVersaoAberta(false)}
                                  onAtualizado={novo => { setProcesso(novo); setVersaoAberta(false); }}/>
            )}
        </div>
    );
};

export default ProcessoAlterar;
